import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryClient'
import {
  connectInstagramByHandle,
  connectWithCode,
  connectWithToken,
  disconnectAccount,
  listAccounts,
  syncAccounts,
  type ConnectInstagramResult,
  type ConnectResult,
  type SyncResult,
} from './api'
import type { SocialAccount } from '@/types/domain'

export function useAccounts(workspaceId: string | null) {
  return useQuery<SocialAccount[]>({
    queryKey: queryKeys.accounts(workspaceId ?? 'none'),
    enabled: Boolean(workspaceId),
    queryFn: () => listAccounts(workspaceId as string),
  })
}

export function useConnectAccount(workspaceId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<ConnectResult, Error, string>({
    mutationFn: (accessToken: string) => connectWithToken(workspaceId as string, accessToken),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts(workspaceId ?? 'none') })
    },
  })
}

/** Código de autorização devolvido pela Meta, com a mesma URL de retorno da ida. */
export interface ConnectWithCodeInput {
  code: string
  redirectUri: string
}

export function useConnectAccountWithCode(workspaceId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<ConnectResult, Error, ConnectWithCodeInput>({
    mutationFn: ({ code, redirectUri }: ConnectWithCodeInput) =>
      connectWithCode(workspaceId as string, code, redirectUri),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts(workspaceId ?? 'none') })
    },
  })
}

/**
 * Adiciona um perfil do Instagram pelo @. O `handle` vai como o usuário
 * digitou: normalizar `@` e espaços é responsabilidade da tela e do servidor.
 */
export function useConnectInstagramByHandle(workspaceId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<ConnectInstagramResult, Error, string>({
    mutationFn: (handle: string) => connectInstagramByHandle(workspaceId as string, handle),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts(workspaceId ?? 'none') })
    },
  })
}

export function useSyncAccounts(workspaceId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<SyncResult, Error, void>({
    mutationFn: () => syncAccounts(workspaceId as string),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts(workspaceId ?? 'none') })
    },
  })
}

export function useDisconnectAccount(workspaceId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (accountId: string) => disconnectAccount(accountId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts(workspaceId ?? 'none') })
    },
  })
}
